// The single source of truth for AmbuNet's pages. Each entry describes one
// page once: what it's called, where it lives, who can see it, where it sits
// in the sidebar, which knowledgebase article/video explains it, and which
// pages relate to it. The sidebar, global search, sitemap, breadcrumbs and
// help drawer all read from here - add a page in this file and every surface
// picks it up.
//
// Field reference:
//   id          stable dot-namespaced key; other entries/sections reference
//               it, so never reuse or rename one casually
//   section     sections.js id - the search chip label and sitemap grouping
//   visibility  { teams, check(ctx), subcontractor, requiredTier } - see
//               visibility.js for exact semantics
//   nav         sidebar metadata; omit it for searchable-only pages.
//               { title?, end?, exclude?, alsoMatch?, suppressActiveStyle?,
//                 check?(ctx), requiredTier?, badge? } - title/check/
//                 requiredTier override the entry's own values for the
//                 sidebar listing only; badge names a /nav-counts key
//   docs        { articleId: '<sectionID>/<id>' in public/docs/index.json,
//                 videoId? }
//   related     entry ids shown as "Related" chips on the page header
//
// ORDER MATTERS: search results tie-break on array position, so keep the
// established order when editing and append new entries to their section
// block's end.

export const REGISTRY = [
  // HOME
  {
    id: 'home.dashboard',
    title: 'Home',
    path: '/',
    section: 'home',
    icon: 'home',
    description: 'Your dashboard with quick links, tasks and active shift.',
    keywords: 'dashboard home main start',
    visibility: { subcontractor: true },
    nav: { end: true, suppressActiveStyle: true },
    docs: { articleId: 'getting-started/home-screen' },
  },
  {
    id: 'home.globalOverview',
    title: 'Global Overview',
    path: '/dashboard',
    section: 'home',
    icon: 'insights',
    description: 'Manager dashboard of activity across the whole organisation.',
    keywords: 'manager overview stats charts insights',
    visibility: { teams: ['Manager', 'Supervisor'] },
    nav: {},
    docs: { articleId: 'getting-started/global-overview' },
    related: ['home.dashboard', 'governance.kpi'],
  },
  {
    id: 'home.myTasks',
    title: 'My Tasks',
    path: '/my-tasks',
    section: 'home',
    icon: 'task',
    description: 'Tasks assigned to you across all modules.',
    keywords: 'todo tasks actions',
    docs: { articleId: 'employees-hr/personal-tools-training-and-crew-vault' },
  },

  // MY WORK
  {
    id: 'myWork.availableShifts',
    title: 'Available Shifts',
    path: '/available-shifts',
    section: 'myWork',
    icon: 'event',
    description: 'Browse and apply for shifts advertised to you.',
    keywords: 'shifts apply book work',
    nav: {},
    docs: { articleId: 'crew/your-shifts' },
    related: ['myWork.myShifts', 'myWork.myAvailability'],
  },
  {
    id: 'myWork.myShifts',
    title: 'My Shifts',
    path: '/my-shifts',
    section: 'myWork',
    icon: 'event',
    description: 'Your upcoming and past shifts, with acknowledgements.',
    keywords: 'rota schedule shifts',
    nav: {},
    docs: { articleId: 'crew/your-shifts' },
    related: ['myWork.availableShifts', 'myWork.myPay'],
  },
  {
    id: 'myWork.myAvailability',
    title: 'My Availability',
    path: '/my-availability',
    section: 'myWork',
    icon: 'eventBusy',
    description: 'Tell your organisation when you are available to work.',
    keywords: 'availability calendar unavailable',
    nav: {},
    docs: { articleId: 'crew/your-availability' },
    related: ['myWork.availableShifts', 'myWork.myShifts'],
  },
  {
    id: 'myWork.myAudits',
    title: 'My Audits',
    path: '/my-audits',
    section: 'myWork',
    icon: 'rule',
    description:
      'Outcomes and feedback from audits of patient records you were involved in.',
    keywords: 'audit feedback epcr record compliance governance',
    docs: { articleId: 'crew/your-training-and-development' },
    related: ['clinical.epcr', 'patientRecords.recordAudits'],
  },
  {
    id: 'myWork.myPay',
    title: 'My Pay',
    path: '/my-pay',
    section: 'myWork',
    icon: 'receiptLong',
    description: 'Your pay history, shift payments and payslip breakdowns.',
    keywords: 'pay wages salary payslip money',
    nav: {},
    docs: { articleId: 'crew/your-pay' },
    related: ['myWork.myShifts'],
  },
  {
    id: 'myWork.myTraining',
    title: 'My Training',
    path: '/mandatory-training',
    section: 'myWork',
    icon: 'school',
    description: 'Your mandatory training courses and completion status.',
    keywords: 'training courses learning elearning',
    nav: {},
    docs: { articleId: 'crew/your-training-and-development' },
    related: ['myWork.myAppraisals', 'patientRecords.myDops'],
  },
  {
    id: 'myWork.myAppraisals',
    title: 'My Appraisals',
    path: '/my-appraisals',
    section: 'myWork',
    icon: 'factCheck',
    description: 'Appraisals and reviews you are part of.',
    keywords: 'appraisal review performance',
    docs: { articleId: 'crew/your-training-and-development' },
    related: ['myWork.myTraining'],
  },
  {
    id: 'myWork.myOccupationalHealth',
    title: 'My Occupational Health',
    path: '/my-occupational-health',
    section: 'myWork',
    icon: 'healthAndSafety',
    description: 'Complete or update your occupational health screening.',
    keywords: 'occupational health screening medical fitness immunisation',
    docs: { articleId: 'crew/your-training-and-development' },
  },

  // WORKFORCE
  {
    id: 'workforce.shiftApplications',
    title: 'Shift Applications',
    path: '/shift-applications',
    section: 'workforce',
    icon: 'personAdd',
    description: 'Review and approve staff applications for advertised shifts.',
    keywords: 'applications approve staff shifts',
    visibility: { teams: ['Personnel', 'Event Planning'] },
    nav: { badge: 'shiftApplications' },
    docs: { articleId: 'event-planning/shift-applications-and-acknowledgement' },
    related: ['workforce.globalRoster', 'myWork.availableShifts'],
  },
  {
    id: 'workforce.globalRoster',
    title: 'Global Roster',
    path: '/global-roster',
    section: 'workforce',
    icon: 'roster',
    description: 'Whole-organisation roster of every shift across all work types.',
    keywords: 'rota roster schedule planning',
    visibility: { teams: ['Personnel'] },
    nav: { badge: 'uncoveredShifts' },
    docs: { articleId: 'employees-hr/rostering-payroll' },
    related: ['workforce.staffAvailability', 'workforce.shiftApplications'],
  },
  {
    id: 'workforce.staffAvailability',
    title: 'Staff Availability',
    path: '/employees/availability',
    section: 'workforce',
    icon: 'eventBusy',
    description: 'See when staff have marked themselves available to work.',
    keywords: 'availability staff planning',
    visibility: { teams: ['Personnel', 'Manager'] },
    nav: {},
    docs: { articleId: 'employees-hr/rostering-payroll' },
    related: ['workforce.globalRoster'],
  },
  {
    id: 'workforce.overtimeClaims',
    title: 'Overtime Claims',
    path: '/overtime-claims',
    section: 'workforce',
    icon: 'timer',
    description: 'Review, approve and undo staff overtime claims.',
    keywords: 'overtime claims pay approve',
    visibility: { teams: ['Personnel'], requiredTier: 2 },
    nav: { badge: 'overtimeClaims' },
    docs: { articleId: 'employees-hr/workforce-scheduling-payroll' },
    related: ['workforce.payroll'],
  },
  {
    id: 'workforce.payroll',
    title: 'Payroll',
    path: '/employees/payroll',
    section: 'workforce',
    icon: 'money',
    description: 'Run payroll, review pay runs and staff pay invoices.',
    keywords: 'payroll pay run wages salary',
    visibility: { teams: ['Personnel'], requiredTier: 2 },
    nav: {},
    docs: { articleId: 'employees-hr/rostering-payroll' },
    related: ['workforce.overtimeClaims', 'myWork.myPay'],
  },

  // COMMAND & CONTROL
  {
    id: 'commandControl.tacticalLogs',
    title: 'Tactical Logs',
    path: '/commandcontrol/tacticallog',
    section: 'commandControl',
    icon: 'tacticalLog',
    description: 'Live tactical decision logging for command incidents.',
    keywords: 'cad command tactical log decision',
    visibility: {
      requiredTier: 2,
      check: (ctx) => !!ctx.cadAccess || ctx.isSiteAdmin,
    },
    nav: {},
    docs: { articleId: 'command-control/logs-and-decisions' },
    related: ['commandControl.controlRoom'],
  },
  {
    id: 'commandControl.controlRoom',
    title: 'Control Room',
    path: '/commandcontrol/controlroom',
    section: 'commandControl',
    icon: 'controlRoom',
    description: 'CAD dispatch board for live incidents, resources and radio log.',
    keywords: 'cad dispatch control room command incidents',
    visibility: {
      requiredTier: 2,
      check: (ctx) => !!ctx.cadAccess || ctx.isSiteAdmin,
    },
    nav: {},
    docs: { articleId: 'command-control/overview' },
    related: ['commandControl.tacticalLogs', 'commandControl.treatmentLocations'],
  },
  {
    id: 'commandControl.treatmentLocations',
    title: 'Treatment Locations',
    path: '/commandcontrol/treatmentlocations',
    section: 'commandControl',
    icon: 'treatment',
    description: 'Manage event treatment centres and patient flow.',
    keywords: 'treatment centre location patients event',
    visibility: { requiredTier: 2 },
    nav: {},
    docs: { articleId: 'command-control/treatment-centres' },
    related: ['commandControl.controlRoom'],
  },

  // EVENTS
  {
    id: 'events.calendar',
    title: 'Event Calendar',
    path: '/events/calendar',
    section: 'events',
    icon: 'event',
    description: 'Calendar of all events with shifts and planning status.',
    keywords: 'events calendar planning',
    nav: {},
    docs: { articleId: 'event-planning/calendar-and-events' },
    related: ['events.proposals', 'customers.clients'],
  },
  {
    id: 'events.proposals',
    title: 'Event Proposals',
    path: '/events/proposals',
    section: 'events',
    icon: 'handshake',
    description: 'Draft and send event proposals and quotes to clients.',
    keywords: 'proposals quotes events sales',
    visibility: { teams: ['Event Planning'] },
    nav: { title: 'Proposals' },
    docs: { articleId: 'event-planning/proposals-and-quotes' },
    related: ['events.calendar', 'customers.clients', 'finance.quotes'],
  },

  // PATIENT TRANSPORT
  {
    id: 'patientTransport.bookings',
    title: 'PTS Bookings',
    path: '/pts/bookings',
    section: 'patientTransport',
    icon: 'schedule',
    description: 'Create and manage patient transport bookings and journeys.',
    keywords: 'pts bookings transport journeys patients',
    visibility: {
      requiredTier: 3,
      check: (ctx) => ctx.ptsAccess.includes('CoOrdinator') || ctx.isSiteAdmin,
    },
    nav: { title: 'Bookings' },
    docs: { articleId: 'patient-transport/bookings' },
    related: ['patientTransport.planning', 'patientTransport.patients'],
  },
  {
    id: 'patientTransport.planning',
    title: 'PTS Planning',
    path: '/pts/planning',
    section: 'patientTransport',
    icon: 'punchClock',
    description: 'Plan PTS duties and allocate journeys to crews.',
    keywords: 'pts planning duties allocation',
    visibility: {
      requiredTier: 3,
      check: (ctx) => ctx.ptsAccess.includes('CoOrdinator') || ctx.isSiteAdmin,
    },
    nav: { title: 'Planning' },
    docs: { articleId: 'patient-transport/planning' },
    related: ['patientTransport.bookings', 'patientTransport.dispatch'],
  },
  {
    id: 'patientTransport.patients',
    title: 'PTS Patients',
    path: '/pts/patients',
    section: 'patientTransport',
    icon: 'patients',
    description: 'Patient records for the transport service.',
    keywords: 'pts patients records',
    visibility: {
      requiredTier: 3,
      check: (ctx) => ctx.ptsAccess.includes('CoOrdinator') || ctx.isSiteAdmin,
    },
    nav: { title: 'Patients' },
    docs: { articleId: 'patient-transport/patients' },
    related: ['patientTransport.bookings'],
  },
  {
    id: 'patientTransport.organisations',
    title: 'PTS Organisations',
    path: '/pts/organisations',
    section: 'patientTransport',
    icon: 'organisation',
    description: 'Hospitals, trusts and other organisations you transport for.',
    keywords: 'pts organisations hospitals contracts tariffs',
    visibility: {
      requiredTier: 3,
      check: (ctx) => ctx.ptsAccess.includes('CoOrdinator') || ctx.isSiteAdmin,
    },
    nav: { title: 'Organisations' },
    docs: { articleId: 'patient-transport/organisations' },
    related: ['patientTransport.invoices'],
  },
  {
    id: 'patientTransport.dispatch',
    title: 'PTS Dispatch',
    path: '/pts/control',
    section: 'patientTransport',
    icon: 'dispatch',
    description: 'Live dispatch board for patient transport crews and journeys.',
    keywords: 'pts dispatch control live tracking',
    visibility: {
      requiredTier: 3,
      check: (ctx) => ctx.ptsAccess.includes('CoOrdinator') || ctx.isSiteAdmin,
    },
    nav: { title: 'Dispatch' },
    docs: { articleId: 'patient-transport/control' },
    related: ['patientTransport.planning', 'patientTransport.myDuty'],
  },
  {
    id: 'patientTransport.reports',
    title: 'PTS Reports',
    path: '/pts/reports',
    section: 'patientTransport',
    icon: 'stats',
    description: 'Performance, SLA and delay reporting for patient transport.',
    keywords: 'pts reports sla performance analytics',
    visibility: {
      requiredTier: 3,
      check: (ctx) => ctx.ptsAccess.includes('CoOrdinator') || ctx.isSiteAdmin,
    },
    nav: { title: 'Reports' },
    docs: { articleId: 'patient-transport/reports' },
    related: ['governance.kpi'],
  },
  {
    id: 'patientTransport.invoices',
    title: 'PTS Invoices',
    path: '/pts/invoices',
    section: 'patientTransport',
    icon: 'receiptLong',
    description: 'Billing runs and invoices for patient transport work.',
    keywords: 'pts invoices billing credit notes',
    visibility: {
      requiredTier: 3,
      check: (ctx) => ctx.ptsAccess.includes('CoOrdinator') || ctx.isSiteAdmin,
    },
    nav: { title: 'Invoices' },
    docs: { articleId: 'patient-transport/invoicing' },
    related: ['patientTransport.organisations', 'finance.invoices'],
  },
  {
    id: 'patientTransport.myDuty',
    title: 'My PTS Duty',
    path: '/pts',
    section: 'patientTransport',
    icon: 'car',
    description: 'Your live PTS duty view with assigned journeys.',
    keywords: 'pts duty crew journeys',
    visibility: {
      requiredTier: 3,
      check: (ctx) => ctx.isEmployee,
    },
    nav: { end: true },
    docs: { articleId: 'patient-transport/crew-view' },
  },

  // PEOPLE
  {
    id: 'people.dashboard',
    title: 'People Dashboard',
    path: '/employeesdashboard',
    section: 'people',
    icon: 'speed',
    description: 'Compliance and HR overview across your workforce.',
    keywords: 'employees dashboard hr compliance',
    visibility: { teams: ['Personnel'] },
    nav: { title: 'Dashboard', end: true },
    docs: { articleId: 'employees-hr/personnel-dashboard' },
    related: ['people.employees'],
  },
  {
    id: 'people.employees',
    title: 'Employees',
    path: '/employees',
    section: 'people',
    icon: 'people',
    description: 'Employee records, compliance documents and role settings.',
    keywords: 'employees staff hr records people',
    visibility: { teams: ['Personnel'] },
    nav: {
      exclude: [
        '/employees/subcontractors',
        '/employees/applications',
        '/employees/mandatory-training',
        '/employees/payroll',
        '/employees/availability',
      ],
    },
    docs: { articleId: 'employees-hr/managing-employees' },
    related: ['people.dashboard', 'people.dbsChecks', 'people.applications'],
  },
  {
    id: 'people.occupationalHealthDashboard',
    title: 'Occupational Health Dashboard',
    path: '/occupational-health',
    section: 'people',
    icon: 'healthAndSafety',
    description:
      'Screening coverage, flagged health declarations, immunisation and fitness breakdowns, and the staff worklist.',
    keywords:
      'occupational health screening dashboard immunisation fitness bmi flags compliance',
    visibility: { teams: ['Personnel', 'Manager', 'Supervisor'] },
    // Sidebar-wise this has only ever been listed for Personnel (the section
    // itself is Personnel-gated), although Managers can reach it via search.
    nav: {
      title: 'Occupational Health',
      end: true,
      check: (ctx) => ctx.hasTeam('Personnel'),
    },
    docs: { articleId: 'employees-hr/occupational-health' },
    related: ['myWork.myOccupationalHealth', 'people.employees'],
  },
  {
    id: 'people.subcontractors',
    title: 'Subcontractors',
    path: '/employees/subcontractors',
    section: 'people',
    icon: 'people',
    description: 'Manage subcontractor organisations and their access windows.',
    keywords: 'subcontractors external access',
    visibility: { teams: ['Personnel'] },
    nav: {},
    docs: { articleId: 'employees-hr/subcontractors' },
    related: ['people.employees'],
  },
  {
    id: 'people.applications',
    title: 'Employee Applications',
    path: '/employees/applications',
    section: 'people',
    icon: 'people',
    description: 'Recruitment applications, onboarding and references.',
    keywords: 'applications recruitment onboarding references',
    visibility: { teams: ['Personnel'], requiredTier: 2 },
    nav: { title: 'Applications' },
    docs: { articleId: 'employees-hr/recruitment-applications' },
    related: ['people.employees'],
  },
  {
    id: 'people.mandatoryTrainingAdmin',
    title: 'Mandatory Training (Admin)',
    path: '/employees/mandatory-training',
    section: 'people',
    icon: 'school',
    description: 'Assign courses and track training compliance across staff.',
    keywords: 'training compliance courses admin',
    visibility: { teams: ['Personnel'], requiredTier: 2 },
    nav: { title: 'Mandatory Training' },
    docs: { articleId: 'employees-hr/personal-tools-training-and-crew-vault' },
    related: ['myWork.myTraining', 'people.employees'],
  },
  {
    id: 'people.dbsChecks',
    title: 'DBS Checks',
    path: '/dbs-checks',
    section: 'people',
    icon: 'dbs',
    description: 'Track DBS checks and renewal dates for your staff.',
    keywords: 'dbs checks vetting disclosure',
    visibility: { teams: ['Personnel'] },
    nav: { end: true },
    docs: { articleId: 'employees-hr/dbs-driving-licence-id' },
    related: ['people.employees'],
  },

  // FLEET & EQUIPMENT
  {
    id: 'fleetEquipment.vehicles',
    title: 'Vehicles',
    path: '/fleet',
    section: 'fleetEquipment',
    icon: 'vehicle',
    description: 'Your fleet: vehicle records, MOT, servicing and history.',
    keywords: 'fleet vehicles ambulance mot servicing',
    visibility: { teams: ['Fleet', 'Make Ready', 'Manager', 'Supervisor'] },
    nav: {},
    docs: { articleId: 'fleet-equipment/vehicles-and-assets' },
    related: ['fleetEquipment.vdis', 'fleetEquipment.defects', 'fleetEquipment.assets'],
  },
  {
    id: 'fleetEquipment.cleaningSchedules',
    title: 'Cleaning Schedules',
    path: '/cleaning-schedules',
    section: 'fleetEquipment',
    icon: 'carWash',
    description: 'Vehicle deep-clean schedules and completion records.',
    keywords: 'cleaning ipc deep clean schedule',
    visibility: { teams: ['Make Ready', 'Manager', 'Supervisor'] },
    nav: {},
    docs: { articleId: 'fleet-equipment/cleaning-schedules' },
    related: ['fleetEquipment.vehicleClean', 'fleetEquipment.makeReady'],
  },
  {
    id: 'fleetEquipment.vdis',
    title: 'VDIs',
    path: '/vdis',
    section: 'fleetEquipment',
    icon: 'noCrash',
    description: 'Vehicle daily inspection records.',
    keywords: 'vdi vehicle daily inspection checks',
    visibility: {
      teams: ['Fleet', 'Manager', 'Supervisor'],
      subcontractor: true,
    },
    nav: {},
    docs: { articleId: 'fleet-equipment/vdis-and-safety-checks' },
    related: ['fleetEquipment.startOfShift', 'fleetEquipment.vehicles'],
  },
  {
    id: 'fleetEquipment.safetyChecks',
    title: 'Safety Checks',
    path: '/safety-checks',
    section: 'fleetEquipment',
    icon: 'factCheck',
    description: 'Recorded vehicle safety checks across the fleet.',
    keywords: 'safety checks inspection roadworthiness garage advisories',
    visibility: { teams: ['Fleet', 'Manager', 'Supervisor'] },
    nav: {},
    docs: { articleId: 'fleet-equipment/vdis-and-safety-checks' },
    related: ['fleetEquipment.vehicles', 'fleetEquipment.vdis'],
  },
  {
    id: 'fleetEquipment.defects',
    title: 'Defects',
    path: '/defects',
    section: 'fleetEquipment',
    icon: 'build',
    description: 'Open and historic defects across vehicles and equipment.',
    keywords: 'defects faults repairs maintenance',
    visibility: {
      teams: ['Fleet', 'Make Ready', 'Governance', 'Manager', 'Supervisor'],
    },
    nav: { badge: 'openDefects' },
    docs: { articleId: 'fleet-equipment/defects' },
    related: ['fleetEquipment.reportDefect', 'fleetEquipment.vehicles'],
  },
  {
    id: 'fleetEquipment.makeReady',
    title: 'Make Ready',
    path: '/make-ready-overview',
    section: 'fleetEquipment',
    icon: 'checklist',
    description: 'Load lists, stock levels and make-ready checks for vehicles.',
    keywords: 'make ready load list stock consumables',
    visibility: {
      teams: ['Make Ready', 'Manager', 'Supervisor'],
      requiredTier: 2,
    },
    nav: {
      alsoMatch: [
        '/make-ready-load-lists',
        '/make-ready-history',
        '/make-ready-restock-queue',
        '/make-ready/',
      ],
    },
    docs: { articleId: 'fleet-equipment/make-ready-and-maintenance' },
    related: ['fleetEquipment.vehicles', 'fleetEquipment.cleaningSchedules'],
  },
  {
    id: 'fleetEquipment.assets',
    title: 'Assets',
    path: '/asset-manager',
    section: 'fleetEquipment',
    icon: 'inventory',
    description: 'Equipment asset register with servicing and QR labels.',
    keywords: 'assets equipment register servicing qr',
    visibility: { teams: ['Fleet', 'Make Ready', 'Manager', 'Supervisor'] },
    nav: {},
    docs: { articleId: 'fleet-equipment/asset-manager' },
    related: ['fleetEquipment.vehicles', 'fleetEquipment.defects'],
  },
  {
    id: 'fleetEquipment.reportDefect',
    title: 'Report a Defect',
    path: '/report-defect',
    section: 'fleetEquipment',
    icon: 'build',
    description: 'Report a fault with a vehicle or piece of equipment.',
    keywords: 'report defect fault broken',
    nav: { title: 'Report Defect' },
    docs: { articleId: 'crew/daily-crew-tools' },
    related: ['fleetEquipment.defects'],
  },
  {
    id: 'fleetEquipment.startOfShift',
    title: 'Start of Shift Check',
    path: '/start-of-shift',
    section: 'fleetEquipment',
    icon: 'checklistRtl',
    description: 'Complete your start-of-shift vehicle and kit checks.',
    keywords: 'start shift check vdi vehicle',
    docs: { articleId: 'crew/daily-crew-tools' },
    related: ['fleetEquipment.vdis', 'fleetEquipment.reportDefect'],
  },
  {
    id: 'fleetEquipment.vehicleClean',
    title: 'Vehicle Clean',
    path: '/vehicle-clean',
    section: 'fleetEquipment',
    icon: 'carWash',
    description: 'Record a vehicle clean.',
    keywords: 'vehicle clean ipc',
    docs: { articleId: 'crew/daily-crew-tools' },
    related: ['fleetEquipment.cleaningSchedules'],
  },
  {
    id: 'fleetEquipment.recordFuel',
    title: 'Record Fuel',
    path: '/record-fuel',
    section: 'fleetEquipment',
    icon: 'fuel',
    description: 'Log a vehicle refuel — litres, price and tank level.',
    keywords: 'fuel refuel diesel petrol litres tank fill',
    docs: { articleId: 'crew/daily-crew-tools' },
    related: ['fleetEquipment.vehicles'],
  },

  // CLINICAL / GOVERNANCE
  {
    id: 'clinical.epcr',
    title: 'Patient Care Record (ePCR)',
    path: '/epcr',
    section: 'clinical',
    icon: 'epcr',
    description: 'Start or continue an electronic patient care record.',
    keywords: 'epcr pcr patient record clinical notes',
    visibility: { subcontractor: true },
    nav: { title: 'ePCR' },
    docs: { articleId: 'clinical-records/starting-and-finding' },
    related: ['patientRecords.careRecords', 'clinical.medicineAdministration'],
  },
  {
    id: 'clinical.checkMedsBag',
    title: 'Check Meds Bag',
    path: '/checkmedsbag',
    section: 'clinical',
    icon: 'medsBag',
    description: 'Check, audit and administer from your medicine bags.',
    keywords: 'meds medicines bag check drugs audit',
    docs: { articleId: 'crew/daily-crew-tools' },
    related: ['governance.medsManagement', 'clinical.medicineAdministration'],
  },
  {
    id: 'clinical.medicineAdministration',
    title: 'Medicine Administration',
    path: '/medicines-management/administration',
    section: 'clinical',
    icon: 'meds',
    description: 'Record a medicine administration to a patient.',
    keywords: 'medicine administration drugs give',
    visibility: { subcontractor: true },
    // Listed in the sidebar only for non-Governance staff, and only while
    // the org hasn't disabled the standalone administration form.
    nav: {
      check: (ctx) =>
        !ctx.hasTeam('Governance') &&
        !ctx.orgSettings?.patientRecordSettings
          ?.disabledMedicinesAdministrationForm,
    },
    docs: { articleId: 'clinical-governance/medicines-management' },
    related: ['clinical.checkMedsBag', 'clinical.epcr'],
  },
  {
    id: 'clinical.reportIncident',
    title: 'Report an Incident',
    path: '/report-incident',
    section: 'clinical',
    icon: 'incident',
    description: 'Report a clinical, operational or safety incident.',
    keywords: 'incident report safety datix',
    visibility: { subcontractor: true },
    nav: { title: 'Incident Reporting' },
    docs: { articleId: 'clinical-governance/incidents-and-safety-alerts' },
    related: ['governance.incidents', 'clinical.speakUp'],
  },
  {
    id: 'clinical.speakUp',
    title: 'Freedom to Speak Up',
    path: '/speak-up',
    section: 'clinical',
    icon: 'speakUp',
    description: 'Raise a concern anonymously, separately from incident reporting.',
    keywords:
      'speak up freedom concern anonymous whistleblow whistleblowing raise worry',
    // Sidebar shows this to non-Governance staff while the org has Freedom to
    // Speak Up switched on; the page itself stays reachable via search.
    nav: {
      title: 'Speak Up',
      check: (ctx) =>
        !ctx.hasTeam('Governance') &&
        ctx.orgSettings?.speakUpSettings?.enabled !== false,
    },
    docs: { articleId: 'clinical-governance/freedom-to-speak-up' },
    related: ['clinical.reportIncident'],
  },
  {
    id: 'governance.speakUpConcerns',
    title: 'Speak Up Concerns',
    path: '/speak-up-concerns',
    section: 'governance',
    icon: 'speakUp',
    description: 'Review concerns raised by staff through Freedom to Speak Up.',
    keywords: 'speak up concerns freedom anonymous whistleblowing governance',
    visibility: { teams: ['Governance'] },
    nav: { badge: 'speakUpConcerns' },
    docs: { articleId: 'clinical-governance/freedom-to-speak-up' },
    related: ['clinical.speakUp', 'governance.incidents'],
  },
  {
    id: 'governance.announcements',
    title: 'Announcements',
    path: '/announcements',
    section: 'governance',
    icon: 'announcement',
    description: 'Organisation-wide announcements and acknowledgements.',
    keywords: 'announcements news notices',
    nav: {},
    docs: { articleId: 'crew/reading-and-acknowledging' },
    related: ['governance.crewVault'],
  },
  {
    id: 'governance.crewVault',
    title: 'Crew Vault',
    path: '/crewvault',
    section: 'governance',
    icon: 'vault',
    description: 'Secure document vault shared with your crews.',
    keywords: 'crew vault documents files',
    nav: { badge: 'unreadCrewVault' },
    docs: { articleId: 'crew/reading-and-acknowledging' },
    related: ['governance.announcements', 'governance.policyLibrary'],
  },
  {
    id: 'governance.audits',
    title: 'Governance Audits',
    path: '/governance/audits',
    section: 'governance',
    icon: 'checklist',
    description: 'Schedule and complete internal governance audits.',
    keywords: 'audits governance compliance checks',
    visibility: { teams: ['Governance'], requiredTier: 2 },
    nav: { title: 'Audits' },
    docs: { articleId: 'clinical-governance/governance-audits' },
    related: ['governance.governanceActions', 'governance.cqcCentre'],
  },
  {
    id: 'governance.cqcCentre',
    title: 'CQC Centre',
    path: '/cqc-centre',
    section: 'governance',
    icon: 'factCheck',
    description: 'Inspection readiness, evidence and quality statements.',
    keywords: 'cqc inspection evidence compliance regulation',
    visibility: { teams: ['Governance'], requiredTier: 2 },
    nav: {},
    docs: { articleId: 'clinical-governance/cqc-centre-overview' },
    related: ['governance.cqcCompliance', 'governance.cqcPack', 'governance.governanceActions'],
  },
  {
    id: 'governance.cqcCompliance',
    title: 'CQC Compliance Questions',
    path: '/cqc-centre/compliance',
    section: 'governance',
    icon: 'factCheck',
    description:
      'Regulation-by-regulation self-assessment: fundamental standards, registration, statutory notifications.',
    keywords:
      'cqc compliance questions regulations fundamental standards statement of purpose safeguarding notifications self assessment',
    visibility: { teams: ['Governance'], requiredTier: 2 },
    docs: { articleId: 'clinical-governance/cqc-registration-centre' },
    related: ['governance.cqcCentre', 'governance.cqcPack'],
  },
  {
    id: 'governance.cqcPack',
    title: 'CQC Inspection Pack',
    path: '/cqc-centre/pack',
    section: 'governance',
    icon: 'factCheck',
    description:
      'Generate the evidence pack an inspector needs - scores, registers, self-assessment.',
    keywords: 'cqc inspection pack pdf evidence export',
    visibility: { teams: ['Governance'], requiredTier: 2 },
    docs: { articleId: 'clinical-governance/cqc-evidence-gaps-actions' },
    related: ['governance.cqcCentre', 'governance.cqcCompliance'],
  },
  {
    id: 'governance.feedback',
    title: 'Feedback',
    path: '/feedback',
    section: 'governance',
    icon: 'feedback',
    description: 'Patient and public feedback, compliments and complaints.',
    keywords: 'feedback complaints compliments patient',
    visibility: { teams: ['Governance'] },
    nav: { badge: 'newFeedback' },
    docs: { articleId: 'clinical-records/patient-feedback' },
    related: ['governance.incidents', 'governance.kpi'],
  },
  {
    id: 'governance.incidents',
    title: 'Incidents',
    path: '/incidents',
    section: 'governance',
    icon: 'incident',
    description: 'Investigate and manage reported incidents.',
    keywords: 'incidents investigations rca safety',
    visibility: { teams: ['Governance'] },
    nav: { badge: 'newIncidents' },
    docs: { articleId: 'clinical-governance/incidents-and-safety-alerts' },
    related: ['clinical.reportIncident', 'governance.safetyAlerts', 'governance.riskRegister'],
  },
  {
    id: 'governance.kpi',
    title: 'KPIs & Analytics',
    path: '/kpi',
    section: 'governance',
    icon: 'speed',
    description: 'Key performance indicators across every module.',
    keywords: 'kpi analytics performance metrics targets',
    visibility: {
      teams: ['Governance', 'Manager', 'Supervisor'],
      requiredTier: 2,
    },
    nav: {},
    docs: { articleId: 'clinical-governance/kpi-tracker' },
    related: ['home.globalOverview', 'governance.governanceActions'],
  },
  {
    id: 'governance.medsManagement',
    title: 'Meds Management',
    path: '/medicines-management',
    section: 'governance',
    icon: 'meds',
    description: 'Medicine stores, bags, stock, audits and administrations.',
    keywords: 'medicines management drugs stock bags stores controlled',
    visibility: { teams: ['Governance'] },
    nav: {},
    docs: { articleId: 'clinical-governance/medicines-management' },
    related: ['governance.drawnUpMedicines', 'clinical.checkMedsBag', 'governance.pgdLibrary'],
  },
  {
    id: 'governance.meetings',
    title: 'Meetings',
    path: '/meetings',
    section: 'governance',
    icon: 'meetings',
    description: 'Governance meetings, agendas, minutes and actions.',
    keywords: 'meetings minutes agenda actions',
    visibility: { teams: ['Governance'], requiredTier: 2 },
    nav: {},
    docs: { articleId: 'clinical-governance/meetings-and-announcements' },
    related: ['governance.governanceActions', 'governance.announcements'],
  },
  {
    id: 'patientRecords.careRecords',
    title: 'Patient Care Records (Admin)',
    path: '/patient-report-forms',
    section: 'patientRecords',
    icon: 'epcr',
    description: 'Review, audit and export completed patient care records.',
    keywords: 'pcr epcr records review audit clinical',
    visibility: { teams: ['Governance'] },
    nav: { title: 'Care Records' },
    docs: { articleId: 'clinical-records/reviewing-and-managing' },
    related: ['patientRecords.recordAudits', 'clinical.epcr'],
  },
  {
    id: 'patientRecords.recordAudits',
    title: 'Record Audits',
    path: '/patient-records/audits',
    section: 'patientRecords',
    icon: 'rule',
    description: 'Audit submitted ePCRs flagged by your audit criteria.',
    keywords: 'epcr audit record quality compliance clinical review',
    visibility: { teams: ['Governance'] },
    nav: {},
    docs: { articleId: 'clinical-records/record-audits' },
    related: ['patientRecords.careRecords', 'myWork.myAudits'],
  },
  {
    id: 'patientRecords.myDops',
    title: 'My DOPS & Logbook',
    path: '/dops',
    section: 'patientRecords',
    icon: 'dops',
    description:
      'DOPS requested of you, your own procedure assessments and your procedure logbook.',
    keywords: 'dops assessment procedure logbook currency intubation cpd reflection',
    visibility: { requiredTier: 3 },
    nav: {},
    docs: { articleId: 'clinical-records/dops-and-logbook' },
    related: ['governance.dops', 'myWork.myTraining'],
  },
  {
    id: 'governance.dops',
    title: 'DOPS & Logbooks',
    path: '/governance/dops',
    section: 'governance',
    icon: 'dops',
    description: 'All procedure assessments and clinician procedure logbooks.',
    keywords: 'dops assessment procedure logbook currency clinician',
    visibility: { teams: ['Governance'], requiredTier: 3 },
    nav: {},
    docs: { articleId: 'clinical-records/dops-and-logbook' },
    related: ['patientRecords.myDops'],
  },
  {
    id: 'governance.myDrawnUpMedicines',
    title: 'My Drawn-up Medicines',
    path: '/drawn-up-medicines',
    section: 'governance',
    icon: 'syringe',
    description: 'Syringes you have drawn up in advance and are carrying.',
    keywords: 'drawn up prepared syringe medicine rsi hems handover discard',
    visibility: { requiredTier: 3 },
    nav: {},
    docs: { articleId: 'crew/daily-crew-tools' },
    related: ['governance.drawnUpMedicines', 'clinical.checkMedsBag'],
  },
  {
    id: 'governance.drawnUpMedicines',
    title: 'Drawn-up Medicines',
    path: '/medicines-management/drawn-up',
    section: 'governance',
    icon: 'syringe',
    description:
      'Every syringe drawn up in advance, who holds it and when it expires.',
    keywords: 'drawn up prepared syringe medicine controlled drug overdue',
    visibility: { teams: ['Governance'], requiredTier: 3 },
    nav: {},
    docs: { articleId: 'clinical-governance/medicines-management' },
    related: ['governance.medsManagement', 'governance.myDrawnUpMedicines'],
  },
  {
    id: 'patientRecords.imaging',
    title: 'Imaging Requests',
    path: '/patient-records/imaging',
    section: 'patientRecords',
    icon: 'imaging',
    description: 'Approve and track imaging studies requested from the ePCR.',
    keywords: 'imaging xray x-ray ultrasound radiology approval dose',
    visibility: { teams: ['Governance'] },
    // Sidebar entry is for the configured imaging approvers, who may sit
    // outside Governance.
    nav: {
      check: (ctx) => {
        const imaging =
          ctx.orgSettings?.patientRecordSettings?.imagingSettings
        return (
          (imaging?.enabled ?? false) &&
          (imaging?.approvers ?? []).some(
            (id) => String(id) === String(ctx.user?._id),
          )
        )
      },
    },
    docs: { articleId: 'clinical-records/imaging-requests' },
    related: ['patientRecords.careRecords'],
  },
  {
    id: 'governance.governanceActions',
    title: 'Governance Actions',
    path: '/governance-actions',
    section: 'governance',
    icon: 'actions',
    description:
      'Open, overdue and completed governance actions from audits, incidents and the CQC Centre.',
    keywords: 'governance actions overdue complete audit action register',
    visibility: { teams: ['Governance'] },
    // The sidebar has always tier-gated this even though the page itself
    // isn't tier-gated.
    nav: { requiredTier: 2 },
    docs: { articleId: 'clinical-governance/governance-actions' },
    related: ['governance.audits', 'governance.cqcCentre', 'governance.meetings'],
  },
  {
    id: 'governance.riskRegister',
    title: 'Risk Register',
    path: '/risk-register',
    section: 'governance',
    icon: 'risk',
    description: 'Organisational risk register with scoring and reviews.',
    keywords: 'risk register scoring mitigation',
    visibility: { teams: ['Governance'], requiredTier: 2 },
    nav: {},
    docs: { articleId: 'clinical-governance/risk-register' },
    related: ['governance.incidents', 'governance.cqcCentre'],
  },
  {
    id: 'governance.safetyAlerts',
    title: 'Safety Alerts',
    path: '/safety-alerts',
    section: 'governance',
    icon: 'alert',
    description: 'Issue and acknowledge safety alerts across the organisation.',
    keywords: 'safety alerts mhra field notices',
    visibility: { teams: ['Governance'] },
    nav: {},
    docs: { articleId: 'clinical-governance/incidents-and-safety-alerts' },
    related: ['governance.incidents', 'governance.announcements'],
  },
  {
    id: 'governance.pgdLibrary',
    title: 'PGD Library',
    path: '/pgd-library',
    section: 'governance',
    icon: 'pgd',
    description: 'Patient group directions with sign-up tracking.',
    keywords: 'pgd patient group directions medicines',
    visibility: { requiredTier: 2 },
    nav: {},
    docs: { articleId: 'crew/reading-and-acknowledging' },
    related: ['governance.medsManagement', 'governance.policyLibrary'],
  },
  {
    id: 'governance.policyLibrary',
    title: 'Policy Library',
    path: '/policy-library',
    section: 'governance',
    icon: 'policy',
    description: 'Organisation policies with version control and sign-off.',
    keywords: 'policies library documents sign off',
    nav: {},
    docs: { articleId: 'crew/reading-and-acknowledging' },
    related: ['governance.sops', 'governance.pgdLibrary'],
  },
  {
    id: 'governance.sops',
    title: 'SOPs & Guidelines',
    path: '/standard-operating-procedures',
    section: 'governance',
    icon: 'sop',
    description: 'Standard operating procedures and clinical guidelines.',
    keywords: 'sop procedures guidelines',
    nav: {},
    docs: { articleId: 'crew/reading-and-acknowledging' },
    related: ['governance.policyLibrary'],
  },

  // CUSTOMERS & FINANCE
  {
    id: 'customers.clients',
    title: 'Clients',
    path: '/clients',
    section: 'customers',
    icon: 'clients',
    description: 'Client organisations, contacts and event history.',
    keywords: 'clients customers organisations crm',
    visibility: { teams: ['Event Planning'] },
    nav: {},
    docs: { articleId: 'event-planning/clients-and-accounts' },
    related: ['events.calendar', 'events.proposals', 'finance.invoices'],
  },
  {
    id: 'finance.centre',
    title: 'Finance Centre',
    path: '/finance',
    section: 'finance',
    icon: 'financeDashboard',
    description: 'Financial overview: income, costs and forecasting.',
    keywords: 'finance money overview forecasting',
    visibility: { teams: ['Finance'] },
    nav: { end: true },
    docs: { articleId: 'finance/overview' },
    related: ['finance.invoices', 'finance.quotes'],
  },
  {
    id: 'finance.invoices',
    title: 'Invoices',
    path: '/invoices',
    section: 'finance',
    icon: 'invoice',
    description: 'Raise and track invoices for clients and events.',
    keywords: 'invoices billing payments',
    visibility: { teams: ['Finance'] },
    nav: {},
    docs: { articleId: 'finance/invoices-and-credit-notes' },
    related: ['finance.quotes', 'finance.centre', 'patientTransport.invoices'],
  },
  {
    id: 'finance.quotes',
    title: 'Quotes',
    path: '/finance/quotes',
    section: 'finance',
    icon: 'quote',
    description: 'Build and send quotes to clients.',
    keywords: 'quotes pricing estimates',
    visibility: { teams: ['Finance'] },
    nav: {},
    docs: { articleId: 'finance/quotes' },
    related: ['finance.invoices', 'events.proposals'],
  },

  // SYSTEM & HELP
  {
    id: 'system.siteConfiguration',
    title: 'Site Configuration',
    path: '/site-configuration',
    section: 'system',
    icon: 'settings',
    description: 'Organisation settings, billing, users and module setup.',
    keywords: 'settings configuration admin setup billing',
    visibility: { teams: ['Manager'] },
    nav: { title: 'Configuration' },
    docs: { articleId: 'getting-started/onboarding' },
    related: ['system.manageAccess'],
  },
  {
    id: 'system.manageAccess',
    title: 'Manage Access',
    path: '/site-configuration/manage-access',
    section: 'system',
    icon: 'access',
    description:
      'Who is in which management team, plus PTS co-ordinator and Control room access, in one grid.',
    keywords:
      'access permissions teams roles matrix manage cad pts control governance personnel',
    visibility: { teams: ['Personnel'] },
    docs: { articleId: 'getting-started/permissions' },
    related: ['system.siteConfiguration', 'people.employees'],
  },
  {
    id: 'system.smsHistory',
    title: 'SMS History',
    path: '/sms-history',
    section: 'system',
    icon: 'sms',
    description: 'Log of SMS messages sent by the platform.',
    keywords: 'sms text messages history',
    visibility: { teams: ['Manager'] },
    nav: {},
  },
  {
    id: 'system.referralProgramme',
    title: 'Referral Programme',
    path: '/site-configuration/billing',
    section: 'system',
    icon: 'referral',
    description:
      'Share your referral code and earn £200 account credit when a referred organisation subscribes.',
    keywords: 'referral refer code credit voucher reward billing',
    visibility: { check: (ctx) => ctx.isSiteAdmin },
  },
  {
    id: 'system.wallboard',
    title: 'Wallboard',
    path: '/wallboard',
    section: 'system',
    icon: 'wallboard',
    description:
      'Full-screen TV display of live shifts, fleet, KPIs and shoutouts.',
    keywords: 'wallboard tv display screen kiosk station dashboard live',
    visibility: {
      requiredTier: 2,
      teams: ['Manager', 'Supervisor'],
    },
    nav: { requiredTier: 2 },
    docs: { articleId: 'getting-started/wallboards' },
    related: ['system.wallboardSettings', 'home.globalOverview'],
  },
  {
    id: 'system.wallboardSettings',
    title: 'Wallboard Settings',
    path: '/wallboard-settings',
    section: 'system',
    icon: 'wallboard',
    description:
      'Build wallboard screens and widgets, and manage authorised displays.',
    keywords: 'wallboard settings screens widgets devices tv display manage',
    visibility: {
      requiredTier: 2,
      check: (ctx) => ctx.isSiteAdmin,
    },
    docs: { articleId: 'getting-started/wallboards' },
    related: ['system.wallboard'],
  },
  {
    id: 'home.baseLocations',
    title: 'Base Locations',
    path: '/base-locations',
    section: 'system',
    icon: 'baseLocations',
    description:
      'Dashboard of your bases with vehicles, staff and COSHH data sheets.',
    keywords: 'base locations sites stations coshh data sheets substances',
    visibility: { teams: ['Manager', 'Supervisor', 'Governance', 'Fleet'] },
    nav: {},
    related: ['fleetEquipment.vehicles', 'people.employees'],
  },
  {
    id: 'help.docs',
    title: 'Documentation',
    path: '/docs',
    section: 'help',
    icon: 'docs',
    description: 'AmbuNet user guides and feature documentation.',
    keywords: 'docs documentation help guides manual',
    visibility: { subcontractor: true },
    nav: { title: 'Knowledgebase' },
    docs: { articleId: 'getting-started/welcome' },
  },
  {
    id: 'help.support',
    title: 'Support',
    path: '/support',
    section: 'help',
    icon: 'support',
    description: 'Contact AmbuNet support or raise a request.',
    keywords: 'support help contact ticket',
    visibility: { subcontractor: true },
    nav: {},
    related: ['help.docs'],
  },

  // Added with the navigation registry (not in the legacy search index)
  {
    id: 'home.notifications',
    title: 'Notifications',
    path: '/notifications',
    section: 'home',
    icon: 'announcement',
    description: 'Your full notification history across every module.',
    keywords: 'notifications inbox alerts history bell',
    visibility: { subcontractor: true },
    docs: { articleId: 'getting-started/finding-your-way' },
    related: ['home.myTasks'],
  },
  {
    id: 'help.sitemap',
    title: 'Sitemap',
    path: '/sitemap',
    section: 'help',
    icon: 'sitemap',
    description: 'Every AmbuNet page you can access, in one place.',
    keywords: 'sitemap all pages index map navigation find',
    visibility: { subcontractor: true },
    docs: { articleId: 'getting-started/finding-your-way' },
    related: ['help.docs'],
  },
]

const byId = new Map(REGISTRY.map((entry) => [entry.id, entry]))

export const getEntryById = (id) => byId.get(id)

export default REGISTRY
